import React from 'react'
import { Navigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useCurrentPractice } from '../contexts/PracticeContext'
import { selectContext } from '../selectors/auth'
import LoadingSpinner from './LoadingSpinner'

type FeatureFlagGuardProps = {
	children: React.ReactNode
	check: (practice: any, consolidator: any) => boolean
}

/**
 * A generic guard component that blocks access based on a feature flag check.
 * The check function receives the current practice and current consolidator.
 */
const FeatureFlagGuard: React.FC<FeatureFlagGuardProps> = ({ children, check }) => {
	const { currentPracticeObject, currentUserConsolidator } = useCurrentPractice()
	const user = useSelector(selectContext)?.user

	if (!currentPracticeObject?.id) {
		return <LoadingSpinner />
	}

	const isEnabled = check(currentPracticeObject, currentUserConsolidator)

	// The consolidator loads after the practice and may still turn the flag on, so wait for it
	// instead of redirecting; otherwise a page refresh bounces practices that inherit the flag.
	// PracticeContext only loads it for users with a consolidatorId, so don't wait without one.
	const consolidatorPending =
		Boolean(currentPracticeObject.consolidatorId && user?.consolidatorId) && // practice has a consolidator and the user is a consolidator user
		!currentUserConsolidator // consolidator hasn't loaded yet

	if (!isEnabled && consolidatorPending) {
		return <LoadingSpinner />
	}

	if (!isEnabled) {
		return <Navigate to="/dashboard" />
	}

	return <>{children}</>
}

export default FeatureFlagGuard
